/**
 * The decisions that stand between a dashboard layout and losing it.
 *
 * Both live here rather than inside the dashboard component so they can be
 * tested without standing up a grid, in the same spirit as [DashboardGrid].
 *
 *  - [hasLocalLayout] — whether this browser already holds layout state of its
 *    own, which decides whether the restore may run immediately or has to wait
 *    for the copy the server is fetching.
 *  - [rejectionReason] — whether a layout about to be written over the last
 *    known-good one is trustworthy at all.
 */
internal object DashboardLayoutStore {

    /**
     * Does this browser already have a layout of its own to restore from?
     *
     * localStorage is only a cache; the copy that follows the user between
     * browsers lives on the server. When all three keys are empty the dashboard
     * knows nothing yet and MUST NOT decide what to show until the server
     * fetch has landed — see the comment at the restore site.
     */
    fun hasLocalLayout(autoSave: String?, namedLayouts: String?, activeName: String?): Boolean =
        !autoSave.isNullOrBlank() || !namedLayouts.isNullOrBlank() || !activeName.isNullOrBlank()

    /**
     * Why [items] must not be persisted over the last known-good layout, or
     * null when it is safe to write.
     *
     * Two ways a save can destroy a layout instead of recording one:
     *
     *  - **A widget listed twice.** Only ever the result of the grid having
     *    been populated twice; replaying it puts two copies of the widget on
     *    the grid, the second at its default width, and the user's sizes are
     *    swamped.
     *  - **Nothing at all.** An empty array is what a torn-down or not-yet-
     *    populated grid reports, and writing it erases the layout for good.
     *    Removing widgets is also a real thing users do, so the callers that
     *    just removed one pass [allowEmpty].
     */
    fun rejectionReason(items: Array<dynamic>, allowEmpty: Boolean): String? {
        if (items.isEmpty()) {
            return if (allowEmpty) null else "it has no widgets in it"
        }
        val seen = mutableSetOf<String>()
        val duplicates = linkedSetOf<String>()
        items.forEach { item ->
            val id = item.id?.toString() ?: return@forEach
            if (!seen.add(id)) duplicates += id
        }
        if (duplicates.isNotEmpty()) {
            return "it lists ${duplicates.sorted().joinToString(", ")} more than once"
        }
        return null
    }

    /**
     * Is writing [layoutJson] a no-op — every destination already holds exactly it?
     *
     * A layout change reaches the dashboard as a Gridstack `change` event, and more
     * than one of those describes the same final arrangement: the library fires one
     * for the interaction itself and the dashboard's own handlers fire again after
     * they have finished tidying up. Writing each time is not just wasted work — it
     * schedules a server sync per write, and it fills the log the user reads with
     * saves that record nothing. So a save that would store what is already stored
     * does not happen at all.
     *
     * [storedNamed] is the JSON of the active named layout's entry, or null when
     * there is no active named layout ([hasActiveName] false) — in which case only
     * the auto-save copy has to match.
     */
    fun isNoOpSave(
        layoutJson: String,
        storedAutoSave: String?,
        storedNamed: String?,
        hasActiveName: Boolean,
    ): Boolean {
        if (storedAutoSave != layoutJson) return false
        return !hasActiveName || storedNamed == layoutJson
    }

    /**
     * Widgets whose width or height differs between [previous] and [current], as
     * `id:WxH→WxH`, for the diagnostics. A resize the user did not ask for is the
     * one thing the save log cannot show on its own — every line looks plausible in
     * isolation, and only the delta between two consecutive saves names the widget
     * that changed and what it changed from.
     */
    fun sizeDeltas(previous: Map<String, String>, current: Map<String, String>): List<String> =
        current.entries
            .filter { (id, size) -> previous[id] != null && previous[id] != size }
            .map { (id, size) -> "$id:${previous[id]}→$size" }
            .sorted()
}
